import { Component, DestroyRef, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { fromEvent, map as rxMap, Observable, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SeasonalCelebrationsService } from 'Src/ng2/shared/services/seasonal-celebrations/seasonal-celebrations.service';
import { ToggleService } from 'Src/ng2/shared/services/toggle/toggle.service';
import { Toggles } from 'Src/ng2/shared/constants/toggles.constant';
import { INavStateConfig, NavStateService } from '../services/nav-state/nav-state.service';
import { GlobalSearchAdapterComponent } from 'Src/ng2/shared/components/global-search-adapter/global-search-adapter.component';
import { UserProfileIconNavigationModule } from 'Src/ng2/shared/components/user-profille-icon-navigation/user-profile-icon-navigation.module';
import { UrlNavigationService } from 'Src/ng2/shared/services/url-navigation/url-navigation.service';
import { NvAriaLabelModule } from 'projects/shared/nvps-libraries/design/directives/nv-aria-label/nv-aria-label.module';
import { NvIconModule } from 'projects/shared/nvps-libraries/design/nv-icon/nv-icon.module';
import { NvIconButtonModule } from 'projects/shared/nvps-libraries/design/nv-icon-button/nv-icon-button.module';
import { NvLogoModule } from 'projects/shared/nvps-libraries/design/nv-logo/nv-logo.module';
import { GlobalEntitySelectorComponent } from 'Src/ng2/shared/components/global-entity-selector/global-entity-selector.component';
import { UserRolePermissionsForModelService } from '../../services/user-role-permissions-for-model/user-role-permissions-for-model.service';
import { IUser } from '../../typings/interfaces/user.interface';
import { getCurrentUser } from 'Src/ng2/store';
import { Store } from '@ngrx/store';
import { ActivatedRoute, Router } from '@angular/router';
import { INotesFlyoutPanelServiceOpts, NotesFlyoutPanelService } from '../../flyouts/notes/notes-flyout-panel.service';
import { NOTE_FLYOUT_MODES } from '../../typings/interfaces/notes_v2.interface';
import { BreakpointService } from 'projects/shared/services/breakpoint-service/breakpoint.service';
import { ENTITY_TYPE, TOpEntityType } from '../../typings/types/entity-type.types';
import { TValidPartnerTypes } from '../../typings/interfaces/partner.interface';
import { ENTITY_MODE, TEntityMode } from '../../typings/types/entity-mode.types';
import { TPortalLocation } from '../../typings/interfaces/portal.interface';
import { SharedEventTrackers } from '../../services/mixpanel/event-trackers/shared-tracking.service';
import { getPortalLocationFromEntityType } from '../../helpers/portal/portal.helper';
import { getPageClassificationFromRoute } from '../../helpers/page-classification/page-classification.helper';
import { ACTIONS_ORIGIN, PORTAL_ACTIONS } from '../../components/nv-actions/nv-actions.interface';
import { PortalActionsService } from '../../components/nv-actions/nv-actions.service';

// TODO - persist mixpanel tracking
@Component({
  selector: 'global-top-nav',
  templateUrl: './global-top-nav.component.html',
  styleUrls: ['./global-top-nav.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    GlobalSearchAdapterComponent,
    NvAriaLabelModule,
    NvIconButtonModule,
    NvIconModule,
    NvLogoModule,
    GlobalEntitySelectorComponent,
    UserProfileIconNavigationModule,
  ],
  providers: [
    UserRolePermissionsForModelService,
    ToggleService,
    UrlNavigationService,
  ],
})
export class GlobalTopNavComponent implements OnInit {
  // ── Inputs ─────────────────────────────────────────────────────────────────
  @Input() isNoteRestricted: boolean = true;
  public canAuthorEntityNote: boolean = false;
  private _topNavRootRef?: ElementRef<HTMLElement>;

  get topNavRootRef (): ElementRef<HTMLElement> | undefined {
    return this._topNavRootRef;
  }

  @ViewChild('topNavRoot')
  set topNavRootRef (ref: ElementRef<HTMLElement> | undefined) {
    this._topNavRootRef = ref;
    const topNavRootEl = ref?.nativeElement;
    if (!topNavRootEl || this.boundTopNavRootEl === topNavRootEl) {
      return;
    }

    this.boundTopNavRootEl = topNavRootEl;
    this.registerCloseOut(this.boundTopNavRootEl);
  }

  /** Seasonal CSS class stream applied to the shell's host container (e.g. `page-banner-default`). */
  navStateConfig$: Observable<INavStateConfig>;
  seasonalClass$: Observable<string>;

  private entityType: TOpEntityType;
  private entityId: string;
  private entityMode: TEntityMode;
  private portal: TPortalLocation;
  private networkId: string;
  private boundTopNavRootEl: HTMLElement | null = null;

  constructor (
    private breakpointService: BreakpointService,
    private navStateService: NavStateService,
    private notesFlyoutPanelService: NotesFlyoutPanelService,
    private route: ActivatedRoute,
    private router: Router,
    private seasonalCelebrationsService: SeasonalCelebrationsService,
    private sharedEventTrackers: SharedEventTrackers,
    private store: Store<any>,
    private portalActionsService: PortalActionsService,
    private toggleService: ToggleService,
    private urlNavigationService: UrlNavigationService,
    private userRolePermissionsForModelService: UserRolePermissionsForModelService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit (): void {
    this.resolveContext();
    this.navStateConfig$ = this.navStateService.getLatestConfig$();
    this.resolveSeasonalClass();
    this.isNoteRestrictedForUser();
  }

  private resolveContext (): void {
    const { entityId, entityType, entityMode, networkId } = UrlNavigationService.getOpContext(this.route.snapshot);
    this.entityId = entityId;
    this.entityType = entityType as TOpEntityType;
    this.entityMode = entityMode;
    this.networkId = networkId;
    this.portal = getPortalLocationFromEntityType(this.entityType, this.entityMode);
  }

  private resolveSeasonalClass (): void {
    const isSummerSchool = this.toggleService.getToggleState(Toggles.TOGGLE_SUMMER_SCHOOL);
    this.seasonalClass$ = this.breakpointService.atLeast$('lg').pipe(
      rxMap((isAtLeastLg) => {
        const isExtended = isAtLeastLg;
        return this.seasonalCelebrationsService.getSeasonalBanner(isExtended, isSummerSchool);
      }),
    );
  }

  // TODO - refactor to use use nv actions, separate card
  /**
   * The control is offered to a single-entity user who may create student notes, and to anyone the
   * backend offers the entity-note action to. Everyone else viewing several entities has no student
   * candidate source, so the blue bar stays restricted for them.
   */
  private isNoteRestrictedForUser (): void {
    this.portalActionsService
      .getPortalActionsConfigs$({
        contextPartnerId: this.entityId,
        contextPartnerType: this.entityType as any as TValidPartnerTypes,
        networkId: this.networkId,
        origin: ACTIONS_ORIGIN.GLOBAL_NAV,
      })
      .pipe(take(1))
      .subscribe((actions) => {
        this.canAuthorEntityNote = (actions || []).some(action => action.key === PORTAL_ACTIONS.ADD_ENTITY_NOTE);
        this.setNoteRestriction();
      });
  }

  private setNoteRestriction (): void {
    if (this.entityMode === ENTITY_MODE.MULTI_ENTITY) {
      this.isNoteRestricted = !this.canAuthorEntityNote;
      return;
    }
    // The shared NOTE permission excludes shelter roles for legacy Portal; One Portal grants shelter
    // users the same note access as school, so the allowance is granted here rather than by widening it.
    const isOnePortalShelter = this.toggleService.getToggleState(Toggles.TOGGLE_ONE_PORTAL) &&
      this.entityType === ENTITY_TYPE.SHELTER;
    if (isOnePortalShelter) {
      this.isNoteRestricted = false;
      return;
    }

    this.store
      .select(getCurrentUser)
      .pipe(take(1))
      .subscribe((user: IUser) => {
        const canCreateNotes = this.userRolePermissionsForModelService.canCreatePartial('NOTE');
        const userCanCreateNotes = canCreateNotes(null, user);
        this.isNoteRestricted = !userCanCreateNotes && !this.canAuthorEntityNote;
      });
  }

  // ── Event handlers ──────────────────────────────────────────────────────────

  onLogoClick (): void {
    const { entityId, entityType, networkId } = UrlNavigationService.getOpContext(this.route.snapshot);
    this.urlNavigationService.navigateToPortal({ entityId, entityType, networkId });
  }

  onBurgerClick (): void {
    this.navStateService.toggleSideNav();
  }

  private registerCloseOut (el: HTMLElement): void {
    fromEvent<MouseEvent>(el, 'click', { capture: true })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => this.closeOut(event));
  }

  private closeOut (event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('.global-top-nav__burger-btn')) {
      return;
    }
    this.navStateService.closeSideNav();
  }

  onAddNote (): void {
    if (!this.isNoteRestricted) {
      const opts: INotesFlyoutPanelServiceOpts = {
        mode: NOTE_FLYOUT_MODES.CREATE,
        contextPartnerId: this.entityId,
        contextPartnerType: this.entityType as any as TValidPartnerTypes,
        entityMode: this.entityMode,
        canAuthorEntityNote: this.canAuthorEntityNote,
      };
      this.notesFlyoutPanelService.openNoteFlyout(opts);

      const { featureSet, pageTemplate } = getPageClassificationFromRoute(this.router.routerState.snapshot.root);
      const urlOrigin = this.router.url.split('?')[0];
      this.sharedEventTrackers.trackUniversalBarAction({
        action: 'ADD_NOTE',
        portal: this.portal,
        featureSet,
        pageTemplate,
        origin: urlOrigin,
      });
    }
  }
}
